/**
 * 본인 비밀번호 변경 모달 (티켓 #415)
 *
 * 헤더 우측 사용자 드롭다운의 "비밀번호 변경"에서 연다 (?modal=change-password).
 * 지금 비밀번호 대조는 서버(/api/v2/auth/change-password)가 하고, 성공하면
 * 모든 세션이 폐기되므로 서버가 돌려준 새 세션으로 이 화면을 이어간다.
 * 새 세션을 못 받으면 로그아웃시키고 새 비밀번호로 다시 로그인하게 안내한다 —
 * 죽은 토큰을 쥔 채 두면 "유효하지 않은 인증 토큰"에 갇힌다(2026-09-17 실사고).
 */

import { useState, type FormEvent } from "react";
import { Modal } from "../ui/modal";
import { changeMyPassword } from "../../api/account";
import { ApiError } from "../../api/_error";
import { supabase } from "../../lib/supabase";

const inputClass =
  "w-full rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-100 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-brand-500 dark:focus:bg-gray-800 dark:focus:ring-brand-500/20";

const labelClass =
  "mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400";

export default function ChangePasswordModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // 어느 칸 아래에 띄울 오류인지 구분한다 — 티켓 명세가 칸별 문구를 지정했다.
  const [currentError, setCurrentError] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [needRelogin, setNeedRelogin] = useState(false);

  function reset() {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setCurrentError("");
    setConfirmError("");
    setFormError("");
    setSaving(false);
    setDone(false);
    setNeedRelogin(false);
  }

  function handleClose() {
    const relogin = needRelogin;
    reset();
    onClose();
    // 세션이 이미 폐기됐다 — 로컬 세션을 비워 로그인 화면으로 보낸다.
    if (relogin) void supabase.auth.signOut({ scope: "local" });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setCurrentError("");
    setConfirmError("");
    setFormError("");

    if (newPassword.length < 8) {
      setFormError("새 비밀번호는 8자 이상 입력해주세요");
      return;
    }
    if (newPassword !== confirmPassword) {
      setConfirmError("두 번 입력한 비밀번호가 다릅니다");
      return;
    }

    setSaving(true);
    try {
      const { sessionKept } = await changeMyPassword(currentPassword, newPassword);
      setNeedRelogin(!sessionKept);
      setDone(true);
    } catch (e) {
      if (
        e instanceof ApiError &&
        (e.payload as { code?: string } | null)?.code === "invalid_current_password"
      ) {
        setCurrentError("지금 비밀번호가 맞지 않습니다");
      } else {
        setFormError(
          e instanceof Error ? e.message : "비밀번호 변경에 실패했습니다",
        );
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className="max-w-md p-6 sm:p-8"
    >
      <h2 className="text-lg font-bold text-gray-800 dark:text-white/90">
        비밀번호 변경
      </h2>

      {done ? (
        <div className="mt-4">
          <p className="rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
            {needRelogin
              ? "비밀번호를 바꿨습니다. 확인을 누르면 로그아웃되니 새 비밀번호로 다시 로그인해주세요."
              : "비밀번호를 바꿨습니다. 다른 PC·폰에서 열어둔 화면은 다시 로그인해야 합니다."}
          </p>
          <button
            type="button"
            onClick={handleClose}
            className="mt-4 w-full rounded-lg bg-brand-500 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-600"
          >
            확인
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <label className={labelClass}>지금 비밀번호</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoFocus
              autoComplete="current-password"
              className={inputClass}
              placeholder="지금 쓰는 비밀번호"
            />
            {currentError && (
              <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">
                {currentError}
              </p>
            )}
          </div>

          <div>
            <label className={labelClass}>새 비밀번호 (8자 이상)</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              maxLength={72}
              autoComplete="new-password"
              className={inputClass}
              placeholder="새 비밀번호"
            />
          </div>

          <div>
            <label className={labelClass}>새 비밀번호 확인</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              className={inputClass}
              placeholder="새 비밀번호를 한 번 더"
            />
            {confirmError && (
              <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">
                {confirmError}
              </p>
            )}
          </div>

          {formError && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600 dark:bg-red-500/10 dark:text-red-400">
              {formError}
            </p>
          )}

          <button
            type="submit"
            disabled={saving}
            className="w-full rounded-lg bg-brand-500 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-50"
          >
            {saving ? "변경 중..." : "비밀번호 변경"}
          </button>
        </form>
      )}
    </Modal>
  );
}
